import { clearSessionPreservingPrintSettings } from "../utils/orderPrintSettings";
import { completeCustomerSale } from "../services/customerService";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsQR from "jsqr";
import {
  FaBars,
  FaCamera,
  FaCashRegister,
  FaClipboardCheck,
  FaExchangeAlt,
  FaFilter,
  FaLock,
  FaQrcode,
  FaSearch,
  FaSignInAlt,
  FaSignOutAlt,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaUndoAlt,
  FaUser,
} from "react-icons/fa";
import { useCheckout } from "../hooks/useCheckout";
import {
  calculateVoucherDiscount,
  fetchCustomerByPhone,
  fetchTrungThuStores,
  fetchTrungThuProductByQR,
  fetchVoucher,
  getTrungThuItemsByPaymentMethod,
  PAYMENT_METHODS,
  submitTrungThuSale,
  submitTrungThuInventoryCheck,
  submitTrungThuStockAction,
} from "../services/posTrungThuService";
import {
  getExpiryDetails,
  getSaleExpiryIssues,
} from "../utils/trungThuExpiry";
import {
  calculateTrungThuDiscount,
  TRUNG_THU_EMPLOYEE_DISCOUNT_LIMIT,
} from "../utils/trungThuDiscount";
import {
  filterTrungThuStockRows,
  sortTrungThuStockRows,
} from "../utils/trungThuStockSort";
import {
  getCameraOptionLabel,
  getVideoInputDevices,
  selectPreferredCameraDevice,
  TRUNG_THU_CAMERA_STORAGE_KEY,
} from "../utils/trungThuCamera";
import {
  getJsQrInversionAttempts,
  getQrScanRegion,
} from "../utils/trungThuQrScanner";
import { getTrungThuSubmitConfirmationMessage } from "../utils/trungThuSubmitConfirmation";
import "./POSTrungThu.css";

const OPERATION_OPTIONS = [
  {
    id: "sale",
    title: "Bán hàng",
    actionLabel: "Bán hàng",
    icon: FaCashRegister,
  },
  {
    id: "transfer",
    title: "Điều chuyển",
    actionLabel: "Điều chuyển",
    icon: FaExchangeAlt,
  },
  {
    id: "return",
    title: "Trả về",
    actionLabel: "Trả về",
    icon: FaUndoAlt,
  },
  {
    id: "inventory",
    title: "Kiểm kê",
    actionLabel: "Kiểm kê",
    icon: FaClipboardCheck,
  },
];

const SALE_CAKE_TYPES = ["Bánh bán", "Bánh đặt"];

const currency = (value) => `${Math.round(Number(value) || 0).toLocaleString()}₫`;
const percent = (value) =>
  `${Number(value || 0).toLocaleString("vi-VN", {
    maximumFractionDigits: 2,
  })}%`;

const TOAST_AUTO_CLOSE_MS = 1500;
const STOCK_REFRESH_COOLDOWN_MS = 30_000;
const STOCK_REFRESH_SPAM_WINDOW_MS = 5_000;
const STOCK_REFRESH_SPAM_CLICK_LIMIT = 3;
const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const SCANNER_BEEP_URL = `${process.env.PUBLIC_URL || ""}/scanner_beep.mp3`;
const TRUNG_THU_CART_STORAGE_KEY = "posTrungThuCart";
const QR_SCAN_THROTTLE_MS = 100;
const NATIVE_QR_JS_FALLBACK_INTERVAL = 3;
const STOCK_DECREASING_OPERATION_IDS = new Set(["sale", "transfer", "return"]);
const PRIMARY_PAYMENT_METHOD_IDS = new Set(["cash", "transfer"]);

const getStoredTrungThuCart = () => {
  try {
    const storedCart = localStorage.getItem(TRUNG_THU_CART_STORAGE_KEY);
    if (!storedCart) return [];

    const parsedCart = JSON.parse(storedCart);
    if (Array.isArray(parsedCart)) {
      return parsedCart.filter(
        (item) =>
          item &&
          typeof item === "object" &&
          String(item.key ?? "").trim(),
      );
    }
  } catch {
    // Dữ liệu cũ không hợp lệ hoặc trình duyệt không cho phép truy cập localStorage.
  }

  try {
    localStorage.removeItem(TRUNG_THU_CART_STORAGE_KEY);
  } catch {
    // Giữ ứng dụng hoạt động nếu localStorage bị vô hiệu hóa.
  }
  return [];
};

const syncStoredTrungThuCart = (items) => {
  try {
    if (items.length > 0) {
      localStorage.setItem(TRUNG_THU_CART_STORAGE_KEY, JSON.stringify(items));
      return;
    }

    localStorage.removeItem(TRUNG_THU_CART_STORAGE_KEY);
  } catch {
    // Giỏ hàng trong phiên hiện tại vẫn hoạt động nếu localStorage bị lỗi.
  }
};

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const stopMediaStream = (stream) => {
  stream?.getTracks().forEach((track) => track.stop());
};

const createQrBarcodeDetector = () => {
  if (typeof window.BarcodeDetector !== "function") return null;

  try {
    return new window.BarcodeDetector({ formats: ["qr_code"] });
  } catch {
    return null;
  }
};

const requestCameraStream = (deviceId = "") =>
  navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      ...(deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: { ideal: "environment" } }),
      width: { ideal: 720 },
      height: { ideal: 720 },
    },
  });

const applyOneXCameraZoom = async (stream) => {
  const videoTrack = stream?.getVideoTracks?.()[0];
  const capabilities = videoTrack?.getCapabilities?.();
  const zoomCapabilities = capabilities?.zoom;
  if (!zoomCapabilities || !videoTrack?.applyConstraints) return;

  const minZoom = Number(zoomCapabilities.min);
  const maxZoom = Number(zoomCapabilities.max);
  if (!Number.isFinite(minZoom) || !Number.isFinite(maxZoom)) return;

  const oneXZoom = Math.min(maxZoom, Math.max(minZoom, 1));
  try {
    await videoTrack.applyConstraints({ advanced: [{ zoom: oneXZoom }] });
  } catch {
    // Một số trình duyệt công bố zoom nhưng không cho áp dụng trên video stream.
  }
};

const normalizeLotId = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const numericValue = Number(text);
  return Number.isFinite(numericValue) ? String(numericValue) : text;
};

const getStockKey = (storeId, productId, loSX) =>
  `${String(storeId ?? "").trim()}\u001f${String(productId ?? "")
    .trim()
    .toUpperCase()}\u001f${normalizeLotId(loSX)}`;

const getItemStock = (stockByItemKey, storeId, item) => {
  if (!stockByItemKey) return null;

  const key = getStockKey(storeId, item?.id, item?.loSX);
  const stock = Number(stockByItemKey[key] ?? 0);
  return Number.isFinite(stock) ? stock : 0;
};

const findInsufficientStockItems = (items, stockByItemKey, storeId) => {
  if (!stockByItemKey) return [];

  return items.flatMap((item) => {
    const requestedQuantity = Math.max(0, Number(item.quantity) || 0);
    const currentStock = getItemStock(stockByItemKey, storeId, item) ?? 0;
    const availableStock = Math.max(0, currentStock);

    if (requestedQuantity <= availableStock) return [];

    return [
      {
        ...item,
        requestedQuantity,
        currentStock,
        availableStock,
        shortage: requestedQuantity - availableStock,
      },
    ];
  });
};

const getInsufficientStockMessage = (stockIssues) => {
  if (stockIssues.length === 0) return "";

  const issueSummary = stockIssues
    .map((issue, index) => {
      const lotLabel = issue.loSX || issue.id || "không xác định";
      const lotPrefix = index === 0 ? "Lô" : "lô";
      return `${lotPrefix} ${lotLabel} (${issue.availableStock}/${issue.requestedQuantity})`;
    })
    .join(", ");

  return `Không đủ tồn, vui lòng kiểm tra lại !`;
};

const fetchCurrentTrungThuStock = async (storeId) => {
  if (!String(storeId || "").trim()) {
    throw new Error("Không xác định được cửa hàng hiện tại.");
  }

  const response = await axios.get(`${API_BASE_URL}/api/btt-ledger/inventory`, {
    params: {
      MaCH: storeId,
      NgayTruyVan: getLocalDateString(),
    },
  });
  const rows = response.data?.dataByStore?.[String(storeId)] || [];

  const stockRowsByItemKey = rows.reduce((rowsByItemKey, row) => {
    const key = getStockKey(row.MaCH ?? storeId, row.MaSP, row.LoSX);
    const currentRow = rowsByItemKey[key];

    rowsByItemKey[key] = {
      ...(currentRow || row),
      MaCH: row.MaCH ?? storeId,
      MaSP: String(row.MaSP ?? "").trim(),
      LoSX: normalizeLotId(row.LoSX),
      TonConLai:
        (Number(currentRow?.TonConLai) || 0) + (Number(row.TonConLai) || 0),
    };
    return rowsByItemKey;
  }, {});

  const stockRows = Object.values(stockRowsByItemKey);
  const stockByItemKey = stockRows.reduce((stockByKey, row) => {
    const key = getStockKey(row.MaCH ?? storeId, row.MaSP, row.LoSX);
    stockByKey[key] = Number(row.TonConLai) || 0;
    return stockByKey;
  }, {});

  return { stockByItemKey, stockRows };
};

const showSingleToast = (type, content, options = {}) => {
  toast.dismiss();
  toast.clearWaitingQueue?.();
  return toast[type](content, {
    autoClose: TOAST_AUTO_CLOSE_MS,
    ...options,
  });
};

const showToast = {
  info: (content, options) => showSingleToast("info", content, options),
  success: (content, options) => showSingleToast("success", content, options),
  error: (content, options) => showSingleToast("error", content, options),
};

const getStoreId = (store) =>
  String(
    store?.IDCH ??
      store?.MaCH ??
      store?.Ma_CuaHang ??
      store?.MaCH_ ??
      store?.MaCuaHang ??
      store?.id ??
      "",
  ).trim();

const getStoreName = (store) =>
  String(
    store?.TenCuaHang ??
      store?.TenCuaHang_Uni ??
      store?.Ten_CH ??
      store?.TenCH ??
      store?.Ten ??
      store?.name ??
      getStoreId(store),
  ).trim();

const getStoreOptionLabel = (store) => {
  const storeId = getStoreId(store);
  const storeName = getStoreName(store);
  return storeId ? `${storeName} (${storeId})` : storeName;
};

const hasTrungThuSession = () =>
  Boolean(localStorage.getItem("token")) && localStorage.getItem("LoaiCH") !== null;

const saveTrungThuLoginSession = (data) => {
  if (!data?.token || !data.user || !Object.prototype.hasOwnProperty.call(data.user, "LoaiCH")) {
    throw new Error("Không tìm thấy thông tin người dùng để đăng nhập.");
  }

  const { user } = data;
  const sessionValues = {
    token: data.token,
    LoaiCH: user.LoaiCH,
    TenCuaHang: user.TenCuaHang,
    IDCH: user.IDCH,
    MaNV: user.MaNV,
    TenUser: user.TenUser,
    MaPB: user.MaPB,
    MaChucVu: user.MaChucVu,
    stk: user.STKNganHang,
    bank_name: user.NganHang,
    Address: user.DiaChi,
    Phone: user.SoDienThoai,
    IpBanhNgot: user.IpBanhNgot,
    IpMayTinh: user.IpMayTinh,
    IpThuNgan: user.IpThuNgan,
    LamTron: user.LamTron,
  };

  Object.entries(sessionValues).forEach(([key, value]) => {
    localStorage.setItem(key, value ?? "");
  });
  localStorage.setItem(
    "IpBep",
    Array.isArray(user.IpBep) ? user.IpBep[0] ?? "" : user.IpBep ?? "",
  );

  if ([3, 4, 145, 170, 187, 250, 16, 11, 321].includes(user.MaPB)) {
    localStorage.setItem("isAdmin", true);
  } else {
    localStorage.setItem("isBakery", true);
  }
};

function TrungThuLoginModal({ show, onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!username.trim() || !password) {
      setError("Vui lòng nhập tên đăng nhập và mật khẩu.");
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      await onLogin(username.trim(), password);
      setPassword("");
    } catch (loginError) {
      setError(loginError?.message || "Không thể đăng nhập. Vui lòng thử lại.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="pos-tt-login-modal"
    >
      <div className="pos-tt-login-hero">
        <div className="pos-tt-login-mark">
          <FaQrcode aria-hidden="true" />
        </div>
        <div>
          <div className="pos-tt-login-eyebrow">VUI LÒNG ĐĂNG NHẬP</div>
          <p>Đăng nhập bằng tài khoản Doticom.</p>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <label className="pos-tt-login-field" htmlFor="pos-tt-login-username">
            <span className="pos-tt-login-label">
              <FaUser aria-hidden="true" />
              Tên đăng nhập
            </span>
            <div className="pos-tt-login-input-wrap">
              <input
                id="pos-tt-login-username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                placeholder="Nhập tên đăng nhập"
                disabled={isSubmitting}
                autoFocus
              />
            </div>
          </label>
          <label className="pos-tt-login-field" htmlFor="pos-tt-login-password">
            <span className="pos-tt-login-label">
              <FaLock aria-hidden="true" />
              Mật khẩu
            </span>
            <div className="pos-tt-login-input-wrap">
              <input
                id="pos-tt-login-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                placeholder="Nhập mật khẩu"
                disabled={isSubmitting}
              />
            </div>
          </label>
          {error && <div className="pos-tt-login-error" role="alert">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="submit" className="pos-tt-login-submit" disabled={isSubmitting}>
            <span>{isSubmitting ? "Đang đăng nhập..." : "Đăng nhập"}</span>
            {!isSubmitting && <FaSignInAlt aria-hidden="true" />}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

function OperationModal({ show, onSelect, onLogout, selectedOperationId }) {
  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="pos-tt-operation-modal"
    >
      <Modal.Body>
        <div className="pos-tt-operation-header">
          <div className="pos-tt-operation-title">Chọn nghiệp vụ</div>
        </div>
        <div className="pos-tt-operation-list">
          {OPERATION_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isActive = selectedOperationId === option.id;
            return (
              <button
                key={option.id}
                type="button"
                className={`pos-tt-operation-option ${isActive ? "is-active" : ""}`}
                aria-pressed={isActive}
                onClick={() => onSelect(option)}
              >
                <span className="pos-tt-operation-icon">
                  <Icon />
                </span>
                <span>{option.title}</span>
              </button>
            );
          })}
          <button
            type="button"
            className="pos-tt-operation-option is-danger"
            onClick={onLogout}
          >
            <span className="pos-tt-operation-icon">
              <FaSignOutAlt />
            </span>
            <span>Đăng xuất</span>
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function SaleCakeTypeModal({ show, onHide, onSelect }) {
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="pos-tt-operation-modal pos-tt-cake-type-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="pos-tt-operation-title">Chọn loại bánh</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="pos-tt-operation-list">
          {SALE_CAKE_TYPES.map((cakeType) => (
            <button
              key={cakeType}
              type="button"
              className="pos-tt-operation-option pos-tt-cake-type-option"
              onClick={() => onSelect(cakeType)}
            >
              {cakeType}
            </button>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
}

function StockInventoryModal({
  show,
  onHide,
  onRefresh,
  stockRows,
  storeId,
  loadedAt,
  loading,
  error,
}) {
  const [searchValue, setSearchValue] = useState("");
  const [sortByExpiry, setSortByExpiry] = useState(false);
  const [refreshCooldownUntil, setRefreshCooldownUntil] = useState(0);
  const [refreshCooldownSeconds, setRefreshCooldownSeconds] = useState(0);
  const recentRefreshClicksRef = useRef([]);

  useEffect(() => {
    if (!show) {
      setSearchValue("");
      setSortByExpiry(false);
    }
  }, [show]);

  useEffect(() => {
    if (refreshCooldownUntil <= Date.now()) return undefined;

    const updateRemainingSeconds = () => {
      setRefreshCooldownSeconds(
        Math.max(0, Math.ceil((refreshCooldownUntil - Date.now()) / 1000)),
      );
    };
    updateRemainingSeconds();

    const intervalId = window.setInterval(updateRemainingSeconds, 1000);
    const timeoutId = window.setTimeout(() => {
      setRefreshCooldownSeconds(0);
      setRefreshCooldownUntil(0);
    }, refreshCooldownUntil - Date.now());

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [refreshCooldownUntil]);

  const handleRefreshStock = () => {
    const now = Date.now();
    if (loading || now < refreshCooldownUntil) return;

    const recentRefreshClicks = recentRefreshClicksRef.current.filter(
      (clickedAt) => now - clickedAt < STOCK_REFRESH_SPAM_WINDOW_MS,
    );

    if (recentRefreshClicks.length >= STOCK_REFRESH_SPAM_CLICK_LIMIT - 1) {
      const cooldownUntil = now + STOCK_REFRESH_COOLDOWN_MS;
      recentRefreshClicksRef.current = [];
      setRefreshCooldownUntil(cooldownUntil);
      setRefreshCooldownSeconds(Math.ceil(STOCK_REFRESH_COOLDOWN_MS / 1000));
      return;
    }

    recentRefreshClicksRef.current = [...recentRefreshClicks, now];
    onRefresh();
  };

  const sortedStockRows = useMemo(
    () => sortTrungThuStockRows(stockRows, { sortByExpiry }),
    [sortByExpiry, stockRows],
  );
  const filteredStockRows = useMemo(
    () =>
      filterTrungThuStockRows(sortedStockRows, {
        hideEmptyStock: sortByExpiry,
        searchValue,
      }),
    [searchValue, sortByExpiry, sortedStockRows],
  );
  const totalStock = useMemo(
    () => stockRows.reduce((total, row) => total + (Number(row.TonConLai) || 0), 0),
    [stockRows],
  );
  const emptyLotCount = useMemo(
    () => stockRows.filter((row) => (Number(row.TonConLai) || 0) <= 0).length,
    [stockRows],
  );
  const availableLotCount = stockRows.length - emptyLotCount;
  const loadedAtLabel = loadedAt
    ? new Date(loadedAt).toLocaleString("vi-VN", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "Chưa xác định";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="pos-tt-stock-modal"
    >
      <Modal.Header closeButton>
        <div className="pos-tt-stock-modal-heading">
          <Modal.Title>Tồn kho hiện tại</Modal.Title>
          <div>
            Cửa hàng {storeId} · Cập nhật {loadedAtLabel}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="pos-tt-stock-overview" aria-label="Tổng quan tồn kho">
          <div className="pos-tt-stock-stat is-primary">
            <span>Tổng tồn</span>
            <strong>{totalStock.toLocaleString("vi-VN")}</strong>
            <small>Sản phẩm</small>
          </div>
          <div className="pos-tt-stock-stat">
            <span>Còn tồn</span>
            <strong>{availableLotCount.toLocaleString("vi-VN")}</strong>
            <small>lô hàng</small>
          </div>
          <div className={`pos-tt-stock-stat ${emptyLotCount > 0 ? "is-alert" : ""}`}>
            <span>Hết / âm tồn</span>
            <strong>{emptyLotCount.toLocaleString("vi-VN")}</strong>
            <small>lô cần chú ý</small>
          </div>
        </div>

        {error && (
          <div className="pos-tt-stock-modal-error" role="alert">
            Không thể cập nhật dữ liệu mới. Đang hiển thị lần tải thành công gần nhất.
          </div>
        )}

        <div className="pos-tt-stock-toolbar">
          <div className="pos-tt-stock-toolbar-controls">
            <label className="pos-tt-stock-search">
              <span className="pos-tt-visually-hidden">Tìm sản phẩm hoặc lô</span>
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Tìm tên, mã sản phẩm hoặc số lô"
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              className={`pos-tt-expiry-sort-button ${
                sortByExpiry ? "is-active" : ""
              }`}
              aria-pressed={sortByExpiry}
              onClick={() => setSortByExpiry((currentValue) => !currentValue)}
            >
              <FaFilter aria-hidden="true" />
              Gần hết hạn
            </button>
          </div>
        </div>

        {loading ? (
          <div className="pos-tt-stock-empty is-loading">
            <FaSyncAlt aria-hidden="true" />
            Đang cập nhật tồn kho...
          </div>
        ) : filteredStockRows.length > 0 ? (
          <div className="pos-tt-stock-list">
            {filteredStockRows.map((row) => {
              const stockQuantity = Number(row.TonConLai) || 0;
              const expiryDetails = getExpiryDetails(row.NgaySD);
              const stockStateLabel =
                stockQuantity < 0
                  ? "Âm tồn"
                  : stockQuantity === 0
                    ? "Hết tồn"
                    : "Còn tồn";

              return (
                <div
                  key={getStockKey(row.MaCH ?? storeId, row.MaSP, row.LoSX)}
                  className={`pos-tt-stock-row ${expiryDetails.statusClassName} ${
                    stockQuantity <= 0 ? "is-empty" : ""
                  }`}
                >
                  <div className="pos-tt-stock-row-main">
                    <div className="pos-tt-stock-row-title">
                      {row.Ten_SP || row.MaSP || "Sản phẩm"}
                    </div>
                    <div className="pos-tt-stock-row-meta">
                      <span className="pos-tt-stock-code">{row.MaSP || "Chưa có mã"}</span>
                      <span className="pos-tt-stock-lot">
                        Lô {row.LoSX || "chưa xác định"}
                      </span>
                      <span
                        className={`pos-tt-stock-expiry ${expiryDetails.statusClassName}`}
                      >
                        {expiryDetails.remainingLabel}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`pos-tt-stock-row-quantity ${
                      stockQuantity <= 0 ? "is-empty" : ""
                    }`}
                  >
                    <span>{stockStateLabel}</span>
                    <strong>{stockQuantity.toLocaleString("vi-VN")}</strong>
                    <small>{row.DVT_SP || "cái"}</small>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="pos-tt-stock-empty">
            {stockRows.length > 0
              ? "Không tìm thấy sản phẩm hoặc lô phù hợp."
              : "Không có dữ liệu tồn kho tại cửa hàng này."}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className={`pos-tt-stock-refresh-button ${loading ? "is-loading" : ""}`}
          onClick={handleRefreshStock}
          disabled={loading || refreshCooldownSeconds > 0}
        >
          <FaSyncAlt aria-hidden="true" />
          {loading
            ? "Đang cập nhật"
            : refreshCooldownSeconds > 0
              ? `Thử lại sau ${refreshCooldownSeconds}s`
              : "Cập nhật tồn kho"}
        </button>
        <button type="button" className="pos-tt-secondary-button" onClick={onHide}>
          Đóng
        </button>
      </Modal.Footer>
    </Modal>
  );
}

function ScannedItemsList({
  items,
  onRemove,
  editableQuantity = false,
  onQuantityChange,
  quantityLabel = "Số lượng",
  minQuantity = 1,
  stockByItemKey = null,
  stockStoreId = "",
  showStock = false,
}) {
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const editingOriginalQuantitiesRef = useRef({});

  useEffect(() => {
    const itemKeys = new Set(items.map((item) => item.key));

    setQuantityDrafts((currentDrafts) => {
      const nextDrafts = Object.fromEntries(
        Object.entries(currentDrafts).filter(([key]) => itemKeys.has(key)),
      );
      return Object.keys(nextDrafts).length === Object.keys(currentDrafts).length
        ? currentDrafts
        : nextDrafts;
    });

    editingOriginalQuantitiesRef.current = Object.fromEntries(
      Object.entries(editingOriginalQuantitiesRef.current).filter(([key]) => itemKeys.has(key)),
    );
  }, [items]);

  const hasQuantityDraft = (key) =>
    Object.prototype.hasOwnProperty.call(quantityDrafts, key);

  const selectQuantityInput = (event) => {
    event.target.select();
  };

  const handleQuantityFocus = (item, event) => {
    editingOriginalQuantitiesRef.current[item.key] = item.quantity;
    setQuantityDrafts((currentDrafts) => ({
      ...currentDrafts,
      [item.key]: String(item.quantity ?? ""),
    }));
    selectQuantityInput(event);
  };

  const handleQuantityChange = (key, nextValue) => {
    setQuantityDrafts((currentDrafts) => ({
      ...currentDrafts,
      [key]: nextValue,
    }));

    if (nextValue !== "") {
      onQuantityChange?.(key, nextValue, minQuantity);
    }
  };

  const handleQuantityBlur = (item) => {
    const draftValue = hasQuantityDraft(item.key)
      ? quantityDrafts[item.key]
      : String(item.quantity ?? "");

    if (draftValue === "") {
      const originalQuantity =
        editingOriginalQuantitiesRef.current[item.key] ?? item.quantity;
      onQuantityChange?.(item.key, originalQuantity, minQuantity);
    } else {
      onQuantityChange?.(item.key, draftValue, minQuantity);
    }

    delete editingOriginalQuantitiesRef.current[item.key];
    setQuantityDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[item.key];
      return nextDrafts;
    });
  };

  if (items.length === 0) {
    return (
      <div className="pos-tt-empty-list">
        Chưa có mã nào được thêm.
      </div>
    );
  }

  return (
    <div className="pos-tt-items-list">
      {items.map((item) => {
        const currentStock = showStock
          ? getItemStock(stockByItemKey, stockStoreId, item)
          : null;
        const availableStock = Math.max(0, Number(currentStock) || 0);
        const requestedQuantity = Math.max(0, Number(item.quantity) || 0);
        const hasInsufficientStock =
          currentStock !== null && requestedQuantity > availableStock;

        return (
          <div
            key={item.key}
            className={`pos-tt-item-row ${hasInsufficientStock ? "has-stock-error" : ""}`}
          >
            <div className="pos-tt-item-main">
              <div className="pos-tt-item-title">{item.name}</div>
              {item.loSX && <div className="pos-tt-item-meta">Mã SP: {item.id} . Số lô: {item.loSX}</div>}
              {currentStock !== null && (
                <div
                  className={`pos-tt-item-stock ${
                    hasInsufficientStock ? "is-insufficient" : ""
                  }`}
                >
                  Tồn khả dụng: {availableStock}
                  {hasInsufficientStock && ` · Thiếu ${requestedQuantity - availableStock}`}
                </div>
              )}
            </div>
            <div className="pos-tt-item-side">
              {editableQuantity ? (
                <label className="pos-tt-qty-input">
                  <span>{quantityLabel}</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={minQuantity}
                    step="1"
                    value={
                      hasQuantityDraft(item.key)
                        ? quantityDrafts[item.key]
                        : item.quantity
                    }
                    onFocus={(event) => handleQuantityFocus(item, event)}
                    onClick={selectQuantityInput}
                    onChange={(event) => handleQuantityChange(item.key, event.target.value)}
                    onBlur={() => handleQuantityBlur(item)}
                  />
                </label>
              ) : (
                <div className="pos-tt-qty">x{item.quantity}</div>
              )}
              <button
                type="button"
                className="pos-tt-delete-btn"
                aria-label={`Xóa ${item.name}`}
                onClick={() => onRemove(item.key)}
              >
                <FaTrash />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SalePaymentPanel({
  totals,
  paymentMethod,
  setPaymentMethod,
  cashGiven,
  setCashGiven,
  setCashEnough,
  onToggleEmployeeDiscount,
  customerPhone,
  setCustomerPhone,
  customerInfo,
  setCustomerInfo,
  customerLoading,
  setCustomerLoading,
  usePoint,
  setUsePoint,
  voucherCode,
  setVoucherCode,
  voucherError,
  setVoucherError,
  setVoucherInfo,
  voucherLoading,
  setVoucherLoading,
  billNote,
  setBillNote,
}) {
  const [showMorePaymentMethods, setShowMorePaymentMethods] = useState(
    () => !PRIMARY_PAYMENT_METHOD_IDS.has(paymentMethod),
  );
  const [showNote, setShowNote] = useState(
    () => Boolean(String(billNote || "").trim()),
  );
  const [showTransferQRZoom, setShowTransferQRZoom] = useState(false);
  const voucherLookupRequest = useRef(0);
  useEffect(() => {
    voucherLookupRequest.current += 1;
    setVoucherInfo(null);
    setVoucherLoading(false);
    return () => { voucherLookupRequest.current += 1; };
  }, [customerInfo?.MaKH, voucherCode, setVoucherInfo, setVoucherLoading]);
  const visiblePaymentMethods = showMorePaymentMethods
    ? PAYMENT_METHODS
    : PAYMENT_METHODS.filter((method) =>
        PRIMARY_PAYMENT_METHOD_IDS.has(method.id),
      );
  const transferAmount = Math.round(Number(totals.total) || 0);
  const appliedDiscountLabel = totals.flashSaleEnabled
    ? "Flash sale"
    : totals.employeeDiscountApplied
      ? "Nhân viên"
      : "Chiết khấu khách sỉ";
  const appliedPercentLabel = totals.appliedDiscountPercents.length
    ? totals.appliedDiscountPercents.map(percent).join(" / ")
    : percent(0);
  const transferQRCodeSrc = `${process.env.REACT_APP_SEPAY_QR_BASE_URL}/img?acc=${
    localStorage.getItem("stk") || ""
  }&template=qronly&bank=${
    localStorage.getItem("bank_name") || ""
  }&amount=${transferAmount}&des=Thanh toan hoa don tai ${
    localStorage.getItem("TenCuaHang") || ""
  }`;

  useEffect(() => {
    if (paymentMethod !== "transfer") {
      setShowTransferQRZoom(false);
    }
  }, [paymentMethod]);

  const handleTransferQRCodeError = (event) => {
    if (event.currentTarget.dataset.fallbackQr === "true") return;

    event.currentTarget.dataset.fallbackQr = "true";
    event.currentTarget.src = "/qr.png";
  };

  const handleSearchCustomer = async () => {
    if (!customerPhone.trim()) {
      showToast.info("Vui lòng nhập số điện thoại.");
      return;
    }

    setCustomerLoading(true);
    try {
      const customer = await fetchCustomerByPhone(customerPhone.trim());
      if (!customer) {
        setCustomerInfo(null);
        localStorage.removeItem("currentCustomerInfo");
        showToast.info("Không tìm thấy khách hàng.");
        return;
      }

      setCustomerInfo(customer);
      localStorage.setItem("currentCustomerInfo", JSON.stringify(customer));
      showToast.success("Đã nhận thông tin thành viên.");
    } catch (error) {
      showToast.error("Không thể kiểm tra thông tin khách hàng.");
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleApplyVoucher = async () => {
    const request = ++voucherLookupRequest.current;
    setVoucherError("");
    setVoucherInfo(null);

    if (!voucherCode.trim()) {
      setVoucherError("Vui lòng nhập voucher.");
      return;
    }

    setVoucherLoading(true);
    try {
      const voucher = await fetchVoucher(voucherCode.trim(), customerInfo);
      if (request !== voucherLookupRequest.current) return;
      if (!voucher) {
        setVoucherError("Voucher không hợp lệ hoặc đã hết hạn.");
        return;
      }

      setVoucherInfo(voucher);
      showToast.success("Đã áp dụng voucher.");
    } catch (error) {
      if (request === voucherLookupRequest.current) setVoucherError(error.message || "Không thể kiểm tra voucher.");
    } finally {
      if (request === voucherLookupRequest.current) setVoucherLoading(false);
    }
  };

  return (
    <div className="pos-tt-sale-panel">
      <section className="pos-tt-sheet-section">
        <div className="pos-tt-section-title">Hình thức thanh toán</div>
        <div id="pos-tt-payment-methods" className="pos-tt-payment-grid">
          {visiblePaymentMethods.map((method) => (
            <button
              type="button"
              key={method.id}
              className={`pos-tt-chip-btn ${
                paymentMethod === method.id ? "is-active" : ""
              }`}
              onClick={() => {
                setPaymentMethod(method.id);
              }}
            >
              {method.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="pos-tt-disclosure-button"
          aria-expanded={showMorePaymentMethods}
          aria-controls="pos-tt-payment-methods"
          onClick={() => setShowMorePaymentMethods((isVisible) => !isVisible)}
        >
          {showMorePaymentMethods ? "Thu gọn" : "Xem thêm"}
        </button>

        {paymentMethod === "cash" && (
          <div className="pos-tt-field-group">
            <label htmlFor="pos-tt-cash-given">Tiền khách đưa</label>
            <div className="pos-tt-input-action">
              <input
                id="pos-tt-cash-given"
                type="number"
                inputMode="numeric"
                min="0"
                value={cashGiven}
                onChange={(event) => setCashGiven(event.target.value)}
                placeholder="0"
              />
              <button
                type="button"
                onClick={setCashEnough}
              >
                Đủ tiền
              </button>
            </div>
            <div className={totals.change < 0 ? "pos-tt-change is-negative" : "pos-tt-change"}>
              Tiền thối: {currency(Math.max(totals.change, 0))}
            </div>
          </div>
        )}

        {paymentMethod === "transfer" && (
          <>
            <div className="pos-tt-transfer-qr-panel">
              <div className="pos-tt-transfer-qr-header">
                <span>Mã QR chuyển khoản</span>
                <b>{currency(transferAmount)}</b>
              </div>
              <button
                type="button"
                className="pos-tt-transfer-qr-button"
                onClick={() => setShowTransferQRZoom(true)}
                aria-label="Phóng to mã QR chuyển khoản"
                title="Phóng to mã QR"
              >
                <img
                  src={transferQRCodeSrc}
                  alt="QR Code Chuyển khoản"
                  className="pos-tt-transfer-qr-image"
                  onError={handleTransferQRCodeError}
                />
              </button>
            </div>

            {showTransferQRZoom && (
              <div
                className="pos-tt-transfer-qr-overlay"
                role="dialog"
                aria-modal="true"
                aria-label="Mã QR chuyển khoản phóng to"
                onClick={() => setShowTransferQRZoom(false)}
              >
                <div
                  className="pos-tt-transfer-qr-zoom-content"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className="pos-tt-transfer-qr-close"
                    onClick={() => setShowTransferQRZoom(false)}
                    aria-label="Đóng mã QR phóng to"
                  >
                    ×
                  </button>
                  <img
                    src={transferQRCodeSrc}
                    alt="QR Code Chuyển khoản"
                    className="pos-tt-transfer-qr-image-large"
                    onError={handleTransferQRCodeError}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <section className="pos-tt-sheet-section">
        <div className="pos-tt-section-title">Chiết khấu</div>
        <div className="pos-tt-discount-card">
          <div className="pos-tt-discount-status">
            <span>{appliedDiscountLabel}</span>
            <strong>{appliedPercentLabel}</strong>
          </div>
          {!totals.employeeDiscountApplied && (
            <div className="pos-tt-discount-groups">
              {totals.groupSubtotals.bonpas > 0 && (
                <span>
                  Bonpas: {currency(totals.groupSubtotals.bonpas)} →{" "}
                  {percent(totals.groupDiscountPercents.bonpas)}
                </span>
              )}
              {totals.groupSubtotals.dongTien > 0 && (
                <span>
                  Đồng Tiến: {currency(totals.groupSubtotals.dongTien)} →{" "}
                  {percent(totals.groupDiscountPercents.dongTien)}
                </span>
              )}
            </div>
          )}
          {totals.flashSaleEnabled ? (
            <small>Đang áp dụng giảm 50% cho chương trình flash sale.</small>
          ) : (
            <>
              <button
                type="button"
                className={`pos-tt-employee-discount-button ${
                  totals.employeeDiscountApplied ? "is-active" : ""
                }`}
                aria-pressed={totals.employeeDiscountApplied}
                onClick={onToggleEmployeeDiscount}
              >
                Nhân viên
              </button>
              <small>
                {totals.employeeDiscountApplied
                  ? "Đang áp dụng giảm 15% cho đơn nhân viên."
                  : totals.employeeDiscountEligible
                    ? "Nhấn Nhân viên để giảm 15% cho đơn này."
                    : `Chỉ áp dụng cho đơn dưới ${currency(
                        TRUNG_THU_EMPLOYEE_DISCOUNT_LIMIT,
                      )}.`}
              </small>
            </>
          )}
        </div>
      </section>

      <section className="pos-tt-sheet-section">
        <button
          type="button"
          className="pos-tt-disclosure-button pos-tt-add-details-button"
          aria-expanded={showNote}
          aria-controls="pos-tt-note-field"
          onClick={() => setShowNote((isVisible) => !isVisible)}
        >
          {showNote ? "Ẩn ghi chú" : "Thêm ghi chú"}
        </button>

        {showNote && (
          <div id="pos-tt-note-field">
            <label className="pos-tt-field-group">
              <span>Ghi chú</span>
              <textarea
                rows={2}
                value={billNote}
                onChange={(event) => setBillNote(event.target.value)}
                placeholder="Ghi chú hóa đơn"
              />
            </label>
          </div>
        )}
      </section>

      <section className="pos-tt-totals">
        <div>
          <span>Tạm tính</span>
          <b>{currency(totals.subtotal)}</b>
        </div>
        {totals.discountAmount > 0 && (
          <div>
            <span>
              {appliedDiscountLabel} ({appliedPercentLabel})
            </span>
            <b>-{currency(totals.discountAmount)}</b>
          </div>
        )}
        {totals.voucherDiscount > 0 && (
          <div>
            <span>Voucher</span>
            <b>-{currency(totals.voucherDiscount)}</b>
          </div>
        )}
        {totals.usePointDiscount > 0 && (
          <div>
            <span>Điểm</span>
            <b>-{currency(totals.usePointDiscount)}</b>
          </div>
        )}
        <div className="pos-tt-grand-total">
          <span>Thành tiền</span>
          <b>{currency(totals.total)}</b>
        </div>
      </section>
    </div>
  );
}

function POSTrungThu() {
  const location = useLocation();
  const navigate = useNavigate();
  const { storeName } = useParams();
  const { isProcessing, runCheckout } = useCheckout();

  const [operation, setOperation] = useState(null);
  const [authSessionVersion, setAuthSessionVersion] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(() => !hasTrungThuSession());
  const [showOperationModal, setShowOperationModal] = useState(() => hasTrungThuSession());
  const [showSaleCakeTypeModal, setShowSaleCakeTypeModal] = useState(false);
  const [saleCakeType, setSaleCakeType] = useState("");
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false);
  const [returnToOperationAfterLogoutCancel, setReturnToOperationAfterLogoutCancel] =
    useState(false);
  const [showConfirmSheet, setShowConfirmSheet] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const [scanLocked, setScanLocked] = useState(false);
  const [scanSuccessVisible, setScanSuccessVisible] = useState(false);
  const [qrProcessing, setQrProcessing] = useState(false);
  const [scannedItems, setScannedItems] = useState(getStoredTrungThuCart);
  const [stockSubmitting, setStockSubmitting] = useState(false);
  const [stockByItemKey, setStockByItemKey] = useState(null);
  const [stockRows, setStockRows] = useState([]);
  const [stockLoadedAt, setStockLoadedAt] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState("");
  const [stockRefreshVersion, setStockRefreshVersion] = useState(0);
  const [manualLoSX, setManualLoSX] = useState("");
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [transferStores, setTransferStores] = useState([]);
  const [transferStoresLoading, setTransferStoresLoading] = useState(false);
  const [transferStoresError, setTransferStoresError] = useState("");
  const [transferStoreSearch, setTransferStoreSearch] = useState("");
  const [selectedTransferStoreId, setSelectedTransferStoreId] = useState("");
  const [isTransferStoreDropdownOpen, setIsTransferStoreDropdownOpen] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cashGiven, setCashGiven] = useState("");
  const [isCashGivenManual, setIsCashGivenManual] = useState(false);
  const [employeeDiscountRequested, setEmployeeDiscountRequested] = useState(false);
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerInfo, setCustomerInfo] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [usePoint, setUsePoint] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [voucherError, setVoucherError] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [billNote, setBillNote] = useState("");
  const [expiryConfirmation, setExpiryConfirmation] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanVideoFrameCallbackRef = useRef(null);
  const scanFallbackTimeoutRef = useRef(null);
  const scanFrameProcessingRef = useRef(false);
  const scanLastAttemptAtRef = useRef(0);
  const nativeQrMissCountRef = useRef(0);
  const jsQrFailedAttemptsRef = useRef(0);
  const barcodeDetectorRef = useRef(undefined);
  const scanLockedRef = useRef(false);
  const qrProcessingRef = useRef(false);
  const processScannedQRRef = useRef(null);
  const cameraRequestIdRef = useRef(0);
  const scannerBeepRef = useRef(null);
  const transferStoreDropdownRef = useRef(null);
  const autoLoSXHandledRef = useRef("");

  const selectedOperation = operation || OPERATION_OPTIONS[0];
  const currentStoreId = String(localStorage.getItem("IDCH") || "").trim();

  useEffect(() => {
    syncStoredTrungThuCart(scannedItems);
  }, [scannedItems]);

  const requiresStockCheck =
    Boolean(operation) && STOCK_DECREASING_OPERATION_IDS.has(selectedOperation.id);
  const canEditQuantity =
    String(localStorage.getItem("MaChucVu") || "").trim() !== "284";
  const minEditableQuantity = selectedOperation.id === "inventory" ? 0 : 1;
  const editableQuantityLabel =
    selectedOperation.id === "inventory" ? "Tồn TT" : "Số lượng";
  const totalQuantity = useMemo(
    () => scannedItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [scannedItems],
  );
  const stockIssues = useMemo(
    () =>
      requiresStockCheck
        ? findInsufficientStockItems(scannedItems, stockByItemKey, currentStoreId)
        : [],
    [currentStoreId, requiresStockCheck, scannedItems, stockByItemKey],
  );
  const hasVerifiedStock = stockByItemKey !== null && !stockLoading && !stockError;
  const hasValidItemQuantity =
    selectedOperation.id === "inventory" || totalQuantity > 0;
  const canOpenConfirmSheet = scannedItems.length > 0 && hasValidItemQuantity;
  const canSubmitOperation =
    canOpenConfirmSheet &&
    (!requiresStockCheck || (hasVerifiedStock && stockIssues.length === 0));
  const stockStatus = useMemo(() => {
    if (!requiresStockCheck) return null;
    if (stockLoading) return null;
    if (stockError) {
      return {
        className: "is-error",
        message: `Không thể kiểm tra tồn kho: ${stockError}`,
      };
    }
    if (stockIssues.length > 0) {
      return {
        className: "is-error",
        message: getInsufficientStockMessage(stockIssues),
      };
    }
    return null;
  }, [
    requiresStockCheck,
    stockError,
    stockIssues,
    stockLoading,
  ]);

  const saleItems = useMemo(
    () => getTrungThuItemsByPaymentMethod(scannedItems, paymentMethod),
    [paymentMethod, scannedItems],
  );
  const discountDetails = useMemo(
    () =>
      calculateTrungThuDiscount(saleItems, {
        employeeDiscountRequested,
      }),
    [employeeDiscountRequested, saleItems],
  );
  const subtotal = discountDetails.subtotal;

  useEffect(() => {
    if (employeeDiscountRequested && !discountDetails.employeeDiscountEligible) {
      setEmployeeDiscountRequested(false);
    }
  }, [discountDetails.employeeDiscountEligible, employeeDiscountRequested]);

  const totals = useMemo(() => {
    const discountAmount = discountDetails.discountAmount;
    const voucherDiscount = calculateVoucherDiscount(voucherInfo, subtotal);
    const customerPoints = Math.max(0, Number(customerInfo?.DiemTichLuy) || 0);
    const maxPointByBill = Math.max(
      subtotal - discountAmount - voucherDiscount,
      0,
    );
    const usePointValue = Math.max(0, Math.floor(Number(usePoint) || 0));
    const usePointDiscount = Math.min(usePointValue, customerPoints, maxPointByBill);
    const total = Math.max(
      subtotal -
        discountAmount -
        voucherDiscount -
        usePointDiscount,
      0,
    );
    const change = (Number(cashGiven) || 0) - total;

    return {
      subtotal,
      discountPercent: discountDetails.effectiveDiscountPercent,
      discountAmount,
      flashSaleEnabled: discountDetails.flashSaleEnabled,
      employeeDiscountEligible: discountDetails.employeeDiscountEligible,
      employeeDiscountApplied: discountDetails.employeeDiscountApplied,
      appliedDiscountPercents: discountDetails.appliedDiscountPercents,
      groupSubtotals: discountDetails.groupSubtotals,
      groupDiscountPercents: discountDetails.groupDiscountPercents,
      discountedItems: discountDetails.discountedItems,
      voucherDiscount,
      usePointDiscount,
      total,
      change,
    };
  }, [
    cashGiven,
    customerInfo,
    discountDetails,
    subtotal,
    usePoint,
    voucherInfo,
  ]);

  const cashEnoughAmount = useMemo(() => String(Math.ceil(totals.total)), [totals.total]);
  const effectiveCashGiven =
    paymentMethod === "cash" && !isCashGivenManual ? cashEnoughAmount : cashGiven;
  const totalsWithEffectiveCash = useMemo(
    () => ({
      ...totals,
      change: (Number(effectiveCashGiven) || 0) - totals.total,
    }),
    [effectiveCashGiven, totals],
  );
  useEffect(() => {
    let isMounted = true;

    if (!requiresStockCheck) {
      setStockByItemKey(null);
      setStockRows([]);
      setStockLoadedAt(null);
      setShowStockModal(false);
      setStockLoading(false);
      setStockError("");
      return () => {
        isMounted = false;
      };
    }

    const loadStock = async () => {
      setStockByItemKey(null);
      setStockLoading(true);
      setStockError("");

      try {
        const {
          stockByItemKey: currentStockByItemKey,
          stockRows: currentStockRows,
        } = await fetchCurrentTrungThuStock(currentStoreId);
        if (isMounted) {
          setStockByItemKey(currentStockByItemKey);
          setStockRows(currentStockRows);
          setStockLoadedAt(Date.now());
        }
      } catch (error) {
        if (isMounted) {
          setStockError(
            error?.response?.data?.message ||
              error.message ||
              "Không thể kiểm tra tồn kho.",
          );
        }
      } finally {
        if (isMounted) {
          setStockLoading(false);
        }
      }
    };

    loadStock();

    return () => {
      isMounted = false;
    };
  }, [currentStoreId, requiresStockCheck, stockRefreshVersion]);

  const availableTransferStores = useMemo(
    () =>
      transferStores.filter((store) => {
        const storeId = getStoreId(store);
        return storeId && storeId !== currentStoreId;
      }),
    [currentStoreId, transferStores],
  );
  const filteredTransferStores = useMemo(() => {
    const keyword = transferStoreSearch.trim().toLowerCase();
    if (!keyword) return availableTransferStores;

    return availableTransferStores.filter((store) => {
      const storeId = getStoreId(store).toLowerCase();
      const storeName = getStoreName(store).toLowerCase();
      return storeId.includes(keyword) || storeName.includes(keyword);
    });
  }, [availableTransferStores, transferStoreSearch]);
  const selectedTransferStore = useMemo(
    () =>
      availableTransferStores.find(
        (store) => getStoreId(store) === String(selectedTransferStoreId),
      ) || null,
    [availableTransferStores, selectedTransferStoreId],
  );
  const handleTransferStoreInputChange = useCallback(
    (nextValue) => {
      setTransferStoreSearch(nextValue);
      setSelectedTransferStoreId("");
      setIsTransferStoreDropdownOpen(true);
    },
    [],
  );

  useEffect(() => {
    const handleClickOutsideTransferStore = (event) => {
      if (
        transferStoreDropdownRef.current &&
        !transferStoreDropdownRef.current.contains(event.target)
      ) {
        setIsTransferStoreDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideTransferStore);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideTransferStore);
  }, []);

  useEffect(() => {
    if (!isTransferStoreDropdownOpen) return undefined;

    const animationFrameId = window.requestAnimationFrame(() => {
      const dropdown = transferStoreDropdownRef.current;
      const modalBody = dropdown?.closest(".modal-body");
      const options = dropdown?.querySelector(".pos-tt-store-options");
      if (!modalBody || !options) return;

      const modalBodyRect = modalBody.getBoundingClientRect();
      const optionsRect = options.getBoundingClientRect();
      const bottomSpacing = 12;
      const hiddenHeight = optionsRect.bottom - modalBodyRect.bottom + bottomSpacing;

      if (hiddenHeight > 0) {
        modalBody.scrollTo({
          top: modalBody.scrollTop + hiddenHeight,
          behavior: "smooth",
        });
      }
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [filteredTransferStores.length, isTransferStoreDropdownOpen]);

  useEffect(() => {
    if (paymentMethod !== "cash" || isCashGivenManual) return;

    setCashGiven((currentCashGiven) =>
      currentCashGiven === cashEnoughAmount ? currentCashGiven : cashEnoughAmount,
    );
  }, [cashEnoughAmount, isCashGivenManual, paymentMethod]);

  const handlePaymentMethodChange = useCallback((nextPaymentMethod) => {
    setPaymentMethod(nextPaymentMethod);
    setIsCashGivenManual(false);
    if (nextPaymentMethod !== "cash") {
      setCashGiven("");
    }
  }, []);

  const handleCashGivenChange = useCallback((nextCashGiven) => {
    setIsCashGivenManual(true);
    setCashGiven(nextCashGiven);
  }, []);

  const handleCashEnough = useCallback(() => {
    setIsCashGivenManual(false);
    setCashGiven(cashEnoughAmount);
  }, [cashEnoughAmount]);

  const handleToggleEmployeeDiscount = useCallback(() => {
    if (subtotal <= 0) {
      showToast.info("Vui lòng thêm sản phẩm trước khi chọn Nhân viên.");
      return;
    }

    if (subtotal >= TRUNG_THU_EMPLOYEE_DISCOUNT_LIMIT) {
      setEmployeeDiscountRequested(false);
      showToast.info("Giảm giá Nhân viên chỉ áp dụng cho đơn dưới 1.000.000₫.");
      return;
    }

    setEmployeeDiscountRequested((isRequested) => !isRequested);
  }, [subtotal]);

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const previousViewport = viewport?.getAttribute("content");
    viewport?.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no",
    );

    const preventGestureZoom = (event) => {
      event.preventDefault();
    };
    const preventMultiTouchZoom = (event) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    document.addEventListener("gesturestart", preventGestureZoom);
    document.addEventListener("gesturechange", preventGestureZoom);
    document.addEventListener("gestureend", preventGestureZoom);
    document.addEventListener("touchmove", preventMultiTouchZoom, { passive: false });

    return () => {
      if (previousViewport) {
        viewport?.setAttribute("content", previousViewport);
      }

      document.removeEventListener("gesturestart", preventGestureZoom);
      document.removeEventListener("gesturechange", preventGestureZoom);
      document.removeEventListener("gestureend", preventGestureZoom);
      document.removeEventListener("touchmove", preventMultiTouchZoom);
    };
  }, []);

  useEffect(() => {
    const storedLoaiCH = localStorage.getItem("LoaiCH");
    const token = localStorage.getItem("token");

    if (!token || storedLoaiCH === null) {
      setShowOperationModal(false);
      setShowSaleCakeTypeModal(false);
      setShowLoginModal(true);
      return;
    }

    setShowLoginModal(false);

    const loaiCH = parseInt(storedLoaiCH, 10);
    const isAdminHcm = localStorage.getItem("HCM_ADMIN") ? 1 : 0;

    if (storeName === "bonpas" && loaiCH !== 0) {
      navigate("/order");
    } else if (storeName === "hcm" && loaiCH !== 3 && isAdminHcm !== 1) {
      navigate("/order");
    } else if (storeName === "dongtien" && loaiCH !== 2 && loaiCH !== 99) {
      navigate("/order");
    }
  }, [authSessionVersion, navigate, storeName]);

  useEffect(() => {
    if (operation?.id !== "transfer" || transferStores.length > 0) {
      return;
    }

    let isMounted = true;
    const loadTransferStores = async () => {
      setTransferStoresLoading(true);
      setTransferStoresError("");

      try {
        const stores = await fetchTrungThuStores();
        if (isMounted) {
          setTransferStores(stores);
        }
      } catch (error) {
        if (isMounted) {
          setTransferStoresError("Không thể tải danh sách cửa hàng.");
          showToast.error("Không thể tải danh sách cửa hàng nhận.");
        }
      } finally {
        if (isMounted) {
          setTransferStoresLoading(false);
        }
      }
    };

    loadTransferStores();

    return () => {
      isMounted = false;
    };
  }, [operation?.id, transferStores.length]);

  const stopCamera = useCallback(() => {
    cameraRequestIdRef.current += 1;

    if (scanVideoFrameCallbackRef.current !== null) {
      videoRef.current?.cancelVideoFrameCallback?.(
        scanVideoFrameCallbackRef.current,
      );
      scanVideoFrameCallbackRef.current = null;
    }

    if (scanFallbackTimeoutRef.current !== null) {
      window.clearTimeout(scanFallbackTimeoutRef.current);
      scanFallbackTimeoutRef.current = null;
    }

    scanFrameProcessingRef.current = false;
    scanLastAttemptAtRef.current = 0;
    nativeQrMissCountRef.current = 0;
    jsQrFailedAttemptsRef.current = 0;

    stopMediaStream(streamRef.current);
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const unlockScanner = useCallback(() => {
    setScanSuccessVisible(false);
    scanLockedRef.current = false;
    setScanLocked(false);
  }, []);

  const showScanSuccessToast = useCallback(() => {
    setScanSuccessVisible(true);
  }, []);

  const playScannerBeep = useCallback(() => {
    const scannerBeep = scannerBeepRef.current;
    if (!scannerBeep) return;

    scannerBeep.currentTime = 0;
    scannerBeep.play().catch(() => {
      // Trình duyệt có thể chặn âm thanh nếu người dùng chưa tương tác với trang.
    });
  }, []);

  const addScannedProduct = useCallback(async (qrData) => {
    const product = await fetchTrungThuProductByQR(qrData);
    setScannedItems((currentItems) => {
      const existingIndex = currentItems.findIndex((item) => item.key === product.key);
      if (existingIndex === -1) {
        return [...currentItems, product];
      }

      return currentItems.map((item, index) =>
        index === existingIndex
          ? { ...item, quantity: (Number(item.quantity) || 0) + 1 }
          : item,
      );
    });
    playScannerBeep();
    return product;
  }, [playScannerBeep]);

  useEffect(() => {
    const loSXFromUrl = new URLSearchParams(location.search).get("losx")?.trim();
    const hasAuthenticatedUser = hasTrungThuSession();
    if (!loSXFromUrl || !hasAuthenticatedUser) return;

    navigate(location.pathname, { replace: true });
    if (autoLoSXHandledRef.current === loSXFromUrl) return;

    autoLoSXHandledRef.current = loSXFromUrl;
    setSaleCakeType("");
    setShowOperationModal(false);
    setShowSaleCakeTypeModal(true);
    qrProcessingRef.current = true;
    setQrProcessing(true);

    const addProductFromUrl = async () => {
      try {
        const product = await addScannedProduct(loSXFromUrl);
        showToast.success(`Đã thêm LoSX ${product.loSX}.`);
      } catch (error) {
        showToast.error(
          error?.response?.data?.message ||
            error.message ||
            "Lỗi xử lý LoSX từ đường dẫn.",
        );
      } finally {
        qrProcessingRef.current = false;
        setQrProcessing(false);
      }
    };

    addProductFromUrl();
  }, [addScannedProduct, authSessionVersion, location.pathname, location.search, navigate]);

  const processScannedQR = useCallback(
    async (qrData) => {
      if (scanLockedRef.current || qrProcessingRef.current) return;

      scanLockedRef.current = true;
      qrProcessingRef.current = true;
      setScanLocked(true);
      setQrProcessing(true);

      try {
        await addScannedProduct(qrData);
        showScanSuccessToast();
      } catch (error) {
        showToast.error(error?.response?.data?.message || error.message || "Lỗi xử lý QR.");
        scanLockedRef.current = false;
        setScanLocked(false);
      } finally {
        qrProcessingRef.current = false;
        setQrProcessing(false);
      }
    },
    [addScannedProduct, showScanSuccessToast],
  );

  useEffect(() => {
    processScannedQRRef.current = processScannedQR;
  }, [processScannedQR]);

  const scanQRFrame = useCallback(async () => {
    if (
      scanLockedRef.current ||
      qrProcessingRef.current ||
      scanFrameProcessingRef.current
    ) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== 4) return;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return;

    scanFrameProcessingRef.current = true;
    const requestId = cameraRequestIdRef.current;

    try {
      const { sourceX, sourceY, sourceSize, analysisSize } = getQrScanRegion(
        width,
        height,
      );
      if (canvas.width !== analysisSize || canvas.height !== analysisSize) {
        canvas.width = analysisSize;
        canvas.height = analysisSize;
      }

      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;

      context.drawImage(
        video,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        analysisSize,
        analysisSize,
      );

      if (barcodeDetectorRef.current === undefined) {
        barcodeDetectorRef.current = createQrBarcodeDetector();
      }

      let qrData = "";
      let shouldRunJsQr = barcodeDetectorRef.current === null;

      if (barcodeDetectorRef.current) {
        try {
          const detectedBarcodes = await barcodeDetectorRef.current.detect(canvas);
          qrData = String(detectedBarcodes?.[0]?.rawValue || "").trim();

          if (qrData) {
            nativeQrMissCountRef.current = 0;
          } else {
            nativeQrMissCountRef.current += 1;
            shouldRunJsQr =
              nativeQrMissCountRef.current %
                NATIVE_QR_JS_FALLBACK_INTERVAL ===
              0;
          }
        } catch {
          barcodeDetectorRef.current = null;
          shouldRunJsQr = true;
        }
      }

      if (!qrData && shouldRunJsQr) {
        const imageData = context.getImageData(
          0,
          0,
          analysisSize,
          analysisSize,
        );
        const code = jsQR(
          imageData.data,
          imageData.width,
          imageData.height,
          {
            inversionAttempts: getJsQrInversionAttempts(
              jsQrFailedAttemptsRef.current,
            ),
          },
        );
        qrData = String(code?.data || "").trim();

        if (qrData) {
          jsQrFailedAttemptsRef.current = 0;
        } else {
          jsQrFailedAttemptsRef.current += 1;
        }
      }

      if (qrData && requestId === cameraRequestIdRef.current) {
        nativeQrMissCountRef.current = 0;
        jsQrFailedAttemptsRef.current = 0;
        processScannedQRRef.current?.(qrData);
      }
    } catch {
      // Bỏ qua frame lỗi và tiếp tục quét frame kế tiếp.
    } finally {
      scanFrameProcessingRef.current = false;
    }
  }, []);

  const scheduleNextScanFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !streamRef.current) return;

    if (typeof video.requestVideoFrameCallback === "function") {
      scanVideoFrameCallbackRef.current = video.requestVideoFrameCallback(
        (timestamp) => {
          scanVideoFrameCallbackRef.current = null;

          if (
            timestamp - scanLastAttemptAtRef.current >=
            QR_SCAN_THROTTLE_MS
          ) {
            scanLastAttemptAtRef.current = timestamp;
            void scanQRFrame();
          }

          scheduleNextScanFrame();
        },
      );
      return;
    }

    scanFallbackTimeoutRef.current = window.setTimeout(() => {
      scanFallbackTimeoutRef.current = null;
      void scanQRFrame();
      scheduleNextScanFrame();
    }, QR_SCAN_THROTTLE_MS);
  }, [scanQRFrame]);

  const activateCameraStream = useCallback(
    async (stream, requestId) => {
      if (requestId !== cameraRequestIdRef.current) {
        stopMediaStream(stream);
        return false;
      }

      streamRef.current = stream;
      await applyOneXCameraZoom(stream);

      if (requestId !== cameraRequestIdRef.current) {
        stopMediaStream(stream);
        return false;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      barcodeDetectorRef.current = undefined;
      scanLastAttemptAtRef.current = 0;
      scheduleNextScanFrame();
      return true;
    },
    [scheduleNextScanFrame],
  );

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Trình duyệt không hỗ trợ camera.");
      return;
    }

    stopCamera();
    const requestId = cameraRequestIdRef.current;
    setCameraSwitching(true);

    let stream = null;
    try {
      setCameraError("");
      stream = await requestCameraStream();

      const currentDeviceId = String(
        stream.getVideoTracks()[0]?.getSettings?.().deviceId || "",
      );
      let videoDevices = [];

      if (navigator.mediaDevices.enumerateDevices) {
        try {
          videoDevices = getVideoInputDevices(
            await navigator.mediaDevices.enumerateDevices(),
          );
        } catch {
          videoDevices = [];
        }
      }

      if (requestId !== cameraRequestIdRef.current) {
        stopMediaStream(stream);
        return;
      }

      setCameraDevices(videoDevices);
      const savedDeviceId = String(
        localStorage.getItem(TRUNG_THU_CAMERA_STORAGE_KEY) || "",
      );
      const preferredCamera = selectPreferredCameraDevice(videoDevices, {
        savedDeviceId,
        currentDeviceId,
      });

      if (
        preferredCamera?.deviceId &&
        preferredCamera.deviceId !== currentDeviceId
      ) {
        stopMediaStream(stream);
        stream = null;

        try {
          stream = await requestCameraStream(preferredCamera.deviceId);
        } catch {
          if (preferredCamera.deviceId === savedDeviceId) {
            localStorage.removeItem(TRUNG_THU_CAMERA_STORAGE_KEY);
          }
          stream = await requestCameraStream();
        }
      }

      const activeDeviceId = String(
        stream.getVideoTracks()[0]?.getSettings?.().deviceId ||
          preferredCamera?.deviceId ||
          currentDeviceId,
      );
      const activated = await activateCameraStream(stream, requestId);
      if (activated) {
        setSelectedCameraId(activeDeviceId);
      }
    } catch (error) {
      stopMediaStream(stream);
      setCameraError("Không thể mở camera. Vui lòng kiểm tra quyền camera.");
    } finally {
      if (requestId === cameraRequestIdRef.current) {
        setCameraSwitching(false);
      }
    }
  }, [activateCameraStream, stopCamera]);

  const handleCameraDeviceChange = useCallback(
    async (event) => {
      const nextDeviceId = event.target.value;
      if (
        !nextDeviceId ||
        nextDeviceId === selectedCameraId ||
        cameraSwitching
      ) {
        return;
      }

      stopCamera();
      const requestId = cameraRequestIdRef.current;
      setCameraSwitching(true);
      setCameraError("");

      let stream = null;
      try {
        stream = await requestCameraStream(nextDeviceId);
        const activeDeviceId = String(
          stream.getVideoTracks()[0]?.getSettings?.().deviceId || nextDeviceId,
        );
        const activated = await activateCameraStream(stream, requestId);
        if (!activated) return;

        setSelectedCameraId(activeDeviceId);
        localStorage.setItem(TRUNG_THU_CAMERA_STORAGE_KEY, activeDeviceId);
      } catch {
        stopMediaStream(stream);
        if (
          localStorage.getItem(TRUNG_THU_CAMERA_STORAGE_KEY) === nextDeviceId
        ) {
          localStorage.removeItem(TRUNG_THU_CAMERA_STORAGE_KEY);
        }

        try {
          stream = await requestCameraStream();
          const fallbackDeviceId = String(
            stream.getVideoTracks()[0]?.getSettings?.().deviceId || "",
          );
          const activated = await activateCameraStream(stream, requestId);
          if (activated) {
            setSelectedCameraId(fallbackDeviceId);
            setCameraError(
              "Không thể mở camera đã chọn. Hệ thống đã quay về camera sau mặc định.",
            );
          }
        } catch {
          stopMediaStream(stream);
          setCameraError("Không thể chuyển camera. Vui lòng đóng và mở lại camera.");
        }
      } finally {
        if (requestId === cameraRequestIdRef.current) {
          setCameraSwitching(false);
        }
      }
    },
    [activateCameraStream, cameraSwitching, selectedCameraId, stopCamera],
  );

  useEffect(() => {
    if (cameraOpen) {
      startCamera();
    }

    return () => stopCamera();
  }, [cameraOpen, startCamera, stopCamera]);

  const closeCamera = useCallback(() => {
    setScanSuccessVisible(false);
    scanLockedRef.current = false;
    setScanLocked(false);
    setQrProcessing(false);
    setCameraSwitching(false);
    setCameraOpen(false);
    stopCamera();
  }, [stopCamera]);

  const resetAllState = useCallback(() => {
    closeCamera();
    syncStoredTrungThuCart([]);
    setScannedItems([]);
    setShowConfirmSheet(false);
    setShowSubmitConfirmModal(false);
    setShowStockModal(false);
    setShowMenuModal(false);
    setStockByItemKey(null);
    setStockRows([]);
    setStockLoadedAt(null);
    setOperation(null);
    setShowOperationModal(true);
    setShowSaleCakeTypeModal(false);
    setSaleCakeType("");
    setPaymentMethod("cash");
    setCashGiven("");
    setIsCashGivenManual(false);
    setEmployeeDiscountRequested(false);
    setCustomerPhone("");
    setCustomerInfo(null);
    setUsePoint("");
    setVoucherCode("");
    setVoucherInfo(null);
    setVoucherError("");
    setBillNote("");
    setExpiryConfirmation(null);
    setManualLoSX("");
    setTransferStoreSearch("");
    setSelectedTransferStoreId("");
    setIsTransferStoreDropdownOpen(false);
    localStorage.removeItem("currentCustomerInfo");
    localStorage.removeItem("currentCustomerUsePoint");
  }, [closeCamera]);

  const handleSelectOperation = (nextOperation) => {
    if (nextOperation.id === "sale") {
      setSaleCakeType("");
      setShowOperationModal(false);
      setShowSaleCakeTypeModal(true);
      return;
    }

    setSaleCakeType("");
    setOperation(nextOperation);
    setShowOperationModal(false);
    if (nextOperation.id !== "transfer") {
      setTransferStoreSearch("");
      setSelectedTransferStoreId("");
      setIsTransferStoreDropdownOpen(false);
    }
  };

  const handleSelectSaleCakeType = (cakeType) => {
    const saleOperation = OPERATION_OPTIONS.find((option) => option.id === "sale");
    setSaleCakeType(cakeType);
    setOperation(saleOperation);
    setShowSaleCakeTypeModal(false);
  };

  const handleCloseSaleCakeTypeModal = () => {
    setShowSaleCakeTypeModal(false);
    setShowOperationModal(true);
  };

  const handleOpenLogoutConfirm = (returnToOperation = false) => {
    setShowMenuModal(false);
    setShowOperationModal(false);
    setReturnToOperationAfterLogoutCancel(returnToOperation);
    setShowLogoutConfirmModal(true);
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirmModal(false);
    setReturnToOperationAfterLogoutCancel(false);

    if (returnToOperationAfterLogoutCancel) {
      setShowOperationModal(true);
    }
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirmModal(false);
    clearSessionPreservingPrintSettings();
    setShowOperationModal(false);
    setShowSaleCakeTypeModal(false);
    setShowLoginModal(true);
    setAuthSessionVersion((currentValue) => currentValue + 1);
  };

  const handleTrungThuLogin = useCallback(
    async (username, password) => {
      const isAdminLogin = username === "admin";
      const response = await fetch(
        `${API_BASE_URL}${isAdminLogin ? "/admin-login" : "/login"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            isAdminLogin
              ? { username, password }
              : { TenUser: username, Password: password },
          ),
        },
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Đã xảy ra lỗi. Vui lòng thử lại.");
      }

      saveTrungThuLoginSession(data);
      setShowLoginModal(false);
      setShowOperationModal(
        !new URLSearchParams(location.search).get("losx")?.trim(),
      );
      setAuthSessionVersion((currentValue) => currentValue + 1);
    },
    [location.search],
  );

  const handleRemoveItem = (key) => {
    setScannedItems((currentItems) => currentItems.filter((item) => item.key !== key));
  };

  const handleItemQuantityChange = (key, nextQuantity, minQuantity = 0) => {
    const safeQuantity = Math.max(minQuantity, Math.floor(Number(nextQuantity) || 0));
    setScannedItems((currentItems) =>
      currentItems.map((item) =>
        item.key === key ? { ...item, quantity: safeQuantity } : item,
      ),
    );
  };

  const handleManualLoSXSubmit = async (event) => {
    event.preventDefault();

    const loSXValue = manualLoSX.trim();
    if (!operation) {
      showToast.info("Vui lòng chọn nghiệp vụ.");
      return;
    }

    if (!loSXValue) {
      showToast.info("Vui lòng nhập LoSX.");
      return;
    }

    if (manualSubmitting || qrProcessingRef.current) return;

    qrProcessingRef.current = true;
    setQrProcessing(true);
    setManualSubmitting(true);

    try {
      const product = await addScannedProduct(loSXValue);
      setManualLoSX("");
      showToast.success(`Đã thêm LoSX ${product.loSX}.`);
    } catch (error) {
      showToast.error(error?.response?.data?.message || error.message || "Lỗi xử lý LoSX.");
    } finally {
      qrProcessingRef.current = false;
      setQrProcessing(false);
      setManualSubmitting(false);
    }
  };

  const validateSale = () => {
    if (!paymentMethod) {
      showToast.info("Vui lòng chọn hình thức thanh toán.");
      return false;
    }

    if (paymentMethod === "cash" && (Number(effectiveCashGiven) || 0) < totals.total) {
      showToast.info("Tiền khách đưa chưa đủ.");
      return false;
    }

    return true;
  };

  const validateOperationSubmission = () => {
    if (scannedItems.length === 0) {
      showToast.info("Chưa có sản phẩm nào để thực hiện.");
      return false;
    }

    if (selectedOperation.id === "transfer") {
      if (!selectedTransferStoreId) {
        showToast.info("Vui lòng chọn cửa hàng nhận.");
        return false;
      }

      if (selectedTransferStoreId === currentStoreId) {
        showToast.info("Cửa hàng nhận phải khác cửa hàng đang điều chuyển.");
        return false;
      }
    }

    if (selectedOperation.id === "sale" && !validateSale()) {
      return false;
    }

    if (selectedOperation.id === "sale" && !saleCakeType) {
      showToast.info("Vui lòng chọn loại bánh.");
      setShowConfirmSheet(false);
      setShowSaleCakeTypeModal(true);
      return false;
    }

    return true;
  };

  const handleSubmitRequest = () => {
    if (!validateOperationSubmission()) return;
    setShowSubmitConfirmModal(true);
  };

  const validateLatestStock = async () => {
    setStockByItemKey(null);
    setStockRows([]);
    setStockLoading(true);
    setStockError("");

    try {
      const {
        stockByItemKey: latestStockByItemKey,
        stockRows: latestStockRows,
      } = await fetchCurrentTrungThuStock(currentStoreId);
      const latestStockIssues = findInsufficientStockItems(
        scannedItems,
        latestStockByItemKey,
        currentStoreId,
      );

      setStockByItemKey(latestStockByItemKey);
      setStockRows(latestStockRows);
      setStockLoadedAt(Date.now());

      if (latestStockIssues.length > 0) {
        showToast.error(getInsufficientStockMessage(latestStockIssues));
        return {
          isValid: false,
          stockRows: latestStockRows,
        };
      }

      return {
        isValid: true,
        stockRows: latestStockRows,
      };
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Không thể kiểm tra tồn kho.";
      setStockError(errorMessage);
      showToast.error(`Không thể kiểm tra tồn kho: ${errorMessage}`);
      return {
        isValid: false,
        stockRows: [],
      };
    } finally {
      setStockLoading(false);
    }
  };

  const handleSubmit = async ({ allowLowExpiry = false } = {}) => {
    if (!validateOperationSubmission()) return;

    let latestStockRows = stockRows;
    if (requiresStockCheck) {
      const latestStockResult = await validateLatestStock();
      if (!latestStockResult.isValid) {
        return;
      }
      latestStockRows = latestStockResult.stockRows;
    }

    if (selectedOperation.id === "sale") {
      const { expiredItems, expiringSoonItems } = getSaleExpiryIssues({
        items: scannedItems,
        stockRows: latestStockRows,
        storeId: currentStoreId,
      });

      if (expiredItems.length > 0) {
        setExpiryConfirmation({
          type: "expired",
          items: expiredItems,
        });
        return;
      }

      if (expiringSoonItems.length > 0 && !allowLowExpiry) {
        setExpiryConfirmation({
          type: "expiringSoon",
          items: expiringSoonItems,
        });
        return;
      }

      try {
        const success = await runCheckout(async () => {
          await submitTrungThuSale({
            customerInfo,
            items: totals.discountedItems,
            cakeType: saleCakeType,
            subtotal: totals.subtotal,
            total: totals.total,
            paymentMethod,
            cashGiven: effectiveCashGiven,
            discountPercent: totals.discountPercent,
            voucherDiscount: totals.voucherDiscount,
            voucherCode: voucherInfo?.MaCK || "",
            usePointDiscount: totals.usePointDiscount,
            billNote,
          });
          return true;
        });

        if (success) {
          completeCustomerSale(localStorage.getItem("IDCH"));
          showToast.success("Bán hàng thành công.");
          resetAllState();
        }
      } catch (error) {
        showToast.error(
          "Không thể bán hàng: " + (error?.response?.data?.message || error.message),
        );
      }
      return;
    }

    if (selectedOperation.id === "inventory") {
      setStockSubmitting(true);
      try {
        await submitTrungThuInventoryCheck({
          items: scannedItems,
        });
        showToast.success("Kiểm kê thành công.");
        resetAllState();
      } catch (error) {
        showToast.error(
          `Kiểm kê chưa hoàn tất: ${error?.response?.data?.message || error.message}`,
        );
      } finally {
        setStockSubmitting(false);
      }
      return;
    }

    setStockSubmitting(true);
    try {
      await submitTrungThuStockAction({
        actionType: selectedOperation.id,
        items: scannedItems,
        targetStoreId: selectedOperation.id === "transfer" ? selectedTransferStoreId : undefined,
        targetStoreName:
          selectedOperation.id === "transfer" ? getStoreName(selectedTransferStore) : undefined,
      });
      showToast.success(`${selectedOperation.actionLabel} thành công.`);
      resetAllState();
    } catch (error) {
      showToast.error(
        `${selectedOperation.actionLabel} chưa hoàn tất: ${
          error?.response?.data?.message || error.message
        }`,
      );
    } finally {
      setStockSubmitting(false);
    }
  };

  const isSubmitting = isProcessing || stockSubmitting || stockLoading;

  return (
    <div className="pos-tt-page">
      <ToastContainer
        position="top-center"
        newestOnTop
        limit={1}
        autoClose={TOAST_AUTO_CLOSE_MS}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
      />
      <audio ref={scannerBeepRef} src={SCANNER_BEEP_URL} preload="auto" />
      <TrungThuLoginModal
        show={showLoginModal}
        onLogin={handleTrungThuLogin}
      />
      <OperationModal
        show={showOperationModal}
        onSelect={handleSelectOperation}
        onLogout={() => handleOpenLogoutConfirm(true)}
        selectedOperationId={operation?.id}
      />
      <SaleCakeTypeModal
        show={showSaleCakeTypeModal}
        onHide={handleCloseSaleCakeTypeModal}
        onSelect={handleSelectSaleCakeType}
      />

      <header className="pos-tt-header">
        <div className="pos-tt-header-main">
          <div className="pos-tt-kicker">POS Trung Thu</div>
          <h1>{operation ? operation.title : "Quét QR / nhập LoSX"}</h1>
        </div>
        <div className="pos-tt-header-actions">
        {operation && (
          <button
            type="button"
            className="pos-tt-change-operation"
            onClick={() => setShowOperationModal(true)}
          >
            Đổi nghiệp vụ
          </button>
        )}
          <button
            type="button"
            className="pos-tt-menu-button"
            onClick={() => setShowMenuModal(true)}
            aria-label="Mở menu"
          >
            <FaBars />
            <span>Menu</span>
          </button>
        </div>
      </header>

      <Modal
        show={showMenuModal}
        onHide={() => setShowMenuModal(false)}
        centered
        dialogClassName="pos-tt-menu-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Menu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <button
            type="button"
            className="pos-tt-menu-option"
            disabled={!hasVerifiedStock}
            onClick={() => {
              setShowMenuModal(false);
              setShowStockModal(true);
            }}
          >
            <span className="pos-tt-menu-option-icon">
              <FaClipboardCheck />
            </span>
            <span className="pos-tt-menu-option-content">
              <strong>Xem tồn kho</strong>
              <small>
                {stockLoading
                  ? "Đang tải dữ liệu tồn kho..."
                  : hasVerifiedStock
                    ? "Xem số lượng hiện tại theo sản phẩm và lô"
                    : "Chưa có dữ liệu tồn kho"}
              </small>
            </span>
          </button>

          <button
            type="button"
            className="pos-tt-menu-option is-danger"
            onClick={() => handleOpenLogoutConfirm(false)}
          >
            <span className="pos-tt-menu-option-icon">
              <FaSignOutAlt />
            </span>
            <span className="pos-tt-menu-option-content">
              <strong>Đăng xuất</strong>
              <small>Thoát khỏi tài khoản hiện tại</small>
            </span>
          </button>
        </Modal.Body>
      </Modal>

      <Modal
        show={showLogoutConfirmModal}
        onHide={handleCancelLogout}
        centered
        dialogClassName="pos-tt-logout-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Xác nhận đăng xuất</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Bạn có chắc muốn đăng xuất khỏi trang?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pos-tt-secondary-button"
            onClick={handleCancelLogout}
          >
            Hủy
          </button>
          <button
            type="button"
            className="pos-tt-logout-confirm-button"
            onClick={handleConfirmLogout}
          >
            Đăng xuất
          </button>
        </Modal.Footer>
      </Modal>

      <main className="pos-tt-content">
        <section className={`pos-tt-scan-stage ${cameraOpen ? "is-camera" : ""}`}>
          {!cameraOpen ? (
            <button
              type="button"
              className="pos-tt-scan-button"
              onClick={() => setCameraOpen(true)}
              disabled={!operation}
            >
              <FaQrcode />
              <span>Quét mã QR</span>
            </button>
          ) : (
            <>
              <video
                ref={videoRef}
                className="pos-tt-video"
                autoPlay
                playsInline
                muted
              />
              <canvas ref={canvasRef} className="pos-tt-canvas" />
              <div className="pos-tt-scan-frame" />
              {cameraDevices.length > 1 && (
                <label className="pos-tt-camera-selector">
                  <FaCamera aria-hidden="true" />
                  <span className="pos-tt-visually-hidden">Chọn camera</span>
                  <select
                    value={selectedCameraId}
                    onChange={handleCameraDeviceChange}
                    disabled={cameraSwitching || qrProcessing}
                    aria-label="Chọn camera quét QR"
                  >
                    {cameraDevices.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {getCameraOptionLabel(device, index)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {scanSuccessVisible && (
                <div className="pos-tt-scan-toast" role="status">
                  <div>Đã quét thành công</div>
                  <button type="button" onClick={unlockScanner}>
                    Quét tiếp
                  </button>
                </div>
              )}
              <button
                type="button"
                className="pos-tt-close-camera"
                aria-label="Đóng camera"
                onClick={closeCamera}
              >
                <FaTimes />
              </button>
              {(cameraSwitching || scanLocked || qrProcessing) && (
                <div className="pos-tt-camera-status">
                  {cameraSwitching
                    ? "Đang mở camera..."
                    : qrProcessing
                      ? "Đang xử lý..."
                      : "Nhấn \"Quét tiếp\" để tiếp tục"}
                </div>
              )}
              {cameraError && <div className="pos-tt-camera-error">{cameraError}</div>}
            </>
          )}
        </section>

        <form className="pos-tt-losx-form" onSubmit={handleManualLoSXSubmit}>
          <label htmlFor="pos-tt-losx-input">Nhập LoSX</label>
          <div className="pos-tt-losx-action">
            <input
              id="pos-tt-losx-input"
              value={manualLoSX}
              onChange={(event) => setManualLoSX(event.target.value)}
              placeholder="Nhập LoSX hoặc mã QR"
              autoComplete="off"
              disabled={!operation || manualSubmitting}
            />
            <button
              type="submit"
              disabled={!operation || manualSubmitting || qrProcessing}
            >
              {manualSubmitting ? "..." : "Thêm"}
            </button>
          </div>
        </form>

        {stockStatus && (
          <div
            className={`pos-tt-stock-status ${stockStatus.className}`}
            role={stockStatus.className === "is-error" ? "alert" : "status"}
          >
            <span>{stockStatus.message}</span>
            <button
              type="button"
              onClick={() =>
                setStockRefreshVersion((currentValue) => currentValue + 1)
              }
            >
              Kiểm tra lại
            </button>
          </div>
        )}

        <div className="pos-tt-count-card">
          <FaCamera />
          <span>Đã thêm</span>
          <strong>{totalQuantity}</strong>
        </div>
      </main>

      <footer className="pos-tt-footer">
        <button
          type="button"
          className="pos-tt-action-button"
          disabled={!operation || !canOpenConfirmSheet}
          onClick={() => setShowConfirmSheet(true)}
        >
          <span>Thực hiện</span>
          <b>({totalQuantity})</b>
        </button>
      </footer>

      <StockInventoryModal
        show={showStockModal}
        onHide={() => setShowStockModal(false)}
        onRefresh={() =>
          setStockRefreshVersion((currentValue) => currentValue + 1)
        }
        stockRows={stockRows}
        storeId={currentStoreId}
        loadedAt={stockLoadedAt}
        loading={stockLoading}
        error={stockError}
      />

      <Modal
        show={showConfirmSheet}
        onHide={() => {
          setShowConfirmSheet(false);
          setIsTransferStoreDropdownOpen(false);
        }}
        centered
        dialogClassName="pos-tt-bottom-sheet"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedOperation.actionLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ScannedItemsList
            items={scannedItems}
            onRemove={handleRemoveItem}
            editableQuantity={canEditQuantity}
            onQuantityChange={handleItemQuantityChange}
            quantityLabel={editableQuantityLabel}
            minQuantity={minEditableQuantity}
            stockByItemKey={stockByItemKey}
            stockStoreId={currentStoreId}
            showStock={requiresStockCheck}
          />

          {stockStatus && (
            <div
              className={`pos-tt-stock-status pos-tt-sheet-stock-status ${stockStatus.className}`}
              role={stockStatus.className === "is-error" ? "alert" : "status"}
            >
              <span>{stockStatus.message}</span>
              {stockStatus.className === "is-error" && (
                <button
                  type="button"
                  onClick={() =>
                    setStockRefreshVersion((currentValue) => currentValue + 1)
                  }
                >
                  Kiểm tra lại
                </button>
              )}
            </div>
          )}

          <div className="pos-tt-sheet-total">
            <span>Tổng số bánh</span>
            <b>{totalQuantity}</b>
          </div>

          {selectedOperation.id === "transfer" && (
            <section className="pos-tt-sheet-section pos-tt-transfer-panel">
              <div className="pos-tt-section-title">Cửa hàng nhận</div>
              <div
                className="pos-tt-field-group pos-tt-store-dropdown"
                ref={transferStoreDropdownRef}
              >
                <input
                  value={
                    isTransferStoreDropdownOpen
                      ? transferStoreSearch
                      : selectedTransferStore
                        ? getStoreOptionLabel(selectedTransferStore)
                        : transferStoreSearch
                  }
                  onChange={(event) => handleTransferStoreInputChange(event.target.value)}
                  onFocus={() => {
                    setTransferStoreSearch("");
                    setIsTransferStoreDropdownOpen(true);
                  }}
                  placeholder={
                    transferStoresLoading
                      ? "Đang tải cửa hàng..."
                      : "Gõ để tìm kiếm cửa hàng..."
                  }
                  disabled={transferStoresLoading}
                  autoComplete="off"
                  aria-label="Chọn cửa hàng nhận"
                  aria-expanded={isTransferStoreDropdownOpen}
                  aria-controls="pos-tt-transfer-stores"
                />
                {isTransferStoreDropdownOpen && (
                  <div
                    id="pos-tt-transfer-stores"
                    className="pos-tt-store-options"
                    role="listbox"
                    aria-label="Danh sách cửa hàng nhận"
                  >
                    {filteredTransferStores.length > 0 ? (
                      filteredTransferStores.map((store) => {
                        const storeId = getStoreId(store);
                        const isSelected = storeId === selectedTransferStoreId;

                        return (
                          <button
                            key={storeId}
                            type="button"
                            className={`pos-tt-store-option ${
                              isSelected ? "is-selected" : ""
                            }`}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => {
                              setSelectedTransferStoreId(storeId);
                              setTransferStoreSearch("");
                              setIsTransferStoreDropdownOpen(false);
                            }}
                          >
                            {getStoreName(store)}
                            <span>({storeId})</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="pos-tt-store-empty">
                        Không tìm thấy cửa hàng
                      </div>
                    )}
                  </div>
                )}
              </div>
              {transferStoresError && (
                <div className="pos-tt-error-text">{transferStoresError}</div>
              )}
            </section>
          )}

          {selectedOperation.id === "sale" && (
            <SalePaymentPanel
              totals={totalsWithEffectiveCash}
              paymentMethod={paymentMethod}
              setPaymentMethod={handlePaymentMethodChange}
              cashGiven={effectiveCashGiven}
              setCashGiven={handleCashGivenChange}
              setCashEnough={handleCashEnough}
              onToggleEmployeeDiscount={handleToggleEmployeeDiscount}
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              customerLoading={customerLoading}
              setCustomerLoading={setCustomerLoading}
              usePoint={usePoint}
              setUsePoint={setUsePoint}
              voucherCode={voucherCode}
              setVoucherCode={setVoucherCode}
              voucherError={voucherError}
              setVoucherError={setVoucherError}
              setVoucherInfo={setVoucherInfo}
              voucherLoading={voucherLoading}
              setVoucherLoading={setVoucherLoading}
              billNote={billNote}
              setBillNote={setBillNote}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pos-tt-secondary-button"
            onClick={() => setShowConfirmSheet(false)}
            disabled={isSubmitting}
          >
            Đóng
          </button>
          <button
            type="button"
            className="pos-tt-submit-button"
            onClick={handleSubmitRequest}
            disabled={isSubmitting || !canSubmitOperation}
          >
            {isSubmitting ? "Đang xử lý..." : selectedOperation.actionLabel}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSubmitConfirmModal}
        onHide={() => setShowSubmitConfirmModal(false)}
        centered
        backdrop="static"
        backdropClassName="pos-tt-submit-confirm-backdrop"
        className="pos-tt-submit-confirm-layer"
        keyboard={!isSubmitting}
        dialogClassName="pos-tt-logout-modal pos-tt-submit-confirm-modal"
      >
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title>Xác nhận nghiệp vụ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {getTrungThuSubmitConfirmationMessage(selectedOperation.id, saleCakeType)}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pos-tt-secondary-button"
            onClick={() => setShowSubmitConfirmModal(false)}
            disabled={isSubmitting}
          >
            Hủy
          </button>
          <button
            type="button"
            className="pos-tt-submit-button"
            onClick={() => {
              setShowSubmitConfirmModal(false);
              handleSubmit();
            }}
            disabled={isSubmitting}
          >
            Xác nhận
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(expiryConfirmation)}
        onHide={() => {
          if (!isSubmitting) setExpiryConfirmation(null);
        }}
        centered
        backdrop="static"
        keyboard={!isSubmitting}
        dialogClassName="pos-tt-expiry-confirm-modal"
      >
        <Modal.Header closeButton={!isSubmitting}>
          <Modal.Title>
            {expiryConfirmation?.type === "expired"
              ? "Không thể bán bánh hết hạn"
              : "Cảnh báo hạn sử dụng thấp"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="pos-tt-expiry-message">
            {expiryConfirmation?.type === "expired"
              ? "Đơn hàng có bánh đã hết hạn sử dụng. Vui lòng xóa bánh khỏi đơn trước khi tiếp tục."
              : "Các bánh sau còn hạn sử dụng dưới 3 ngày:"}
          </p>
          <div className="pos-tt-expiry-issue-list">
            {expiryConfirmation?.items.map((item, index) => (
              <div
                key={`${item.key || item.loSX || item.id}-${index}`}
                className={`pos-tt-expiry-issue ${
                  expiryConfirmation.type === "expired"
                    ? "is-expired"
                    : "is-expiring-soon"
                }`}
              >
                <b>{item.name}</b>
                <span>
                  Lô {item.loSX || "chưa xác định"} · HSD {item.dateLabel} ·{" "}
                  {item.remainingLabel}
                </span>
              </div>
            ))}
          </div>
          {expiryConfirmation?.type === "expiringSoon" && (
            <p className="pos-tt-expiry-question">
              Bạn có muốn tiếp tục bán?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pos-tt-secondary-button"
            onClick={() => setExpiryConfirmation(null)}
            disabled={isSubmitting}
          >
            {expiryConfirmation?.type === "expired" ? "Đóng" : "Hủy"}
          </button>
          {expiryConfirmation?.type === "expiringSoon" && (
            <button
              type="button"
              className="pos-tt-expiry-continue-button"
              onClick={() => {
                setExpiryConfirmation(null);
                handleSubmit({ allowLowExpiry: true });
              }}
              disabled={isSubmitting}
            >
              Tiếp tục bán
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default POSTrungThu;
